import type { Vec2 } from "./types";
import {
  boundingBox,
  distance,
  isSimplePolygon,
  nearestPointOnSegment,
  polygonArea,
  snap,
  unionPolygons,
} from "./geom";

/** Default grid snap (matches `SNAP_INCHES` in RoomEditorCanvas.tsx). */
export const SKETCH_SNAP_INCHES = 3;

/** Uniform arc-length spacing when resampling the raw stroke (inches). */
export const SKETCH_RESAMPLE_SPACING_IN = 4;

/** Min RDP tolerance (inches); scales up with room size in `processStroke`. */
export const SKETCH_MIN_RDP_TOLERANCE_IN = 3;

/** Angle threshold (degrees) above which a turn becomes a corner. */
export const SKETCH_CORNER_ANGLE_DEG = 20;

/** Segments within ±this many degrees of an axis snap to it. */
export const SKETCH_ORTHO_SNAP_DEG = 22.5;

/** Endpoint within this many inches of the start auto-closes the polygon. */
export const SKETCH_CLOSE_THRESHOLD_IN = 12;

/** Or within this fraction of the bounding box diagonal. */
export const SKETCH_CLOSE_BBOX_FRACTION = 0.06;

/** Minimum room area in square inches (16 ft²). */
export const SKETCH_MIN_AREA_SQ_IN = 16 * 144;

/** Minimum area for an alcove/closet addition (1 ft²). The full-room minimum
 *  is meant to gate against accidental scribbles when defining a whole room
 *  and is way too aggressive for a 24"×36" closet (~6 ft²). */
export const SKETCH_ADDITION_MIN_AREA_SQ_IN = 1 * 144;

/** Tighter resample spacing for additions so a 1-foot scribble still has
 *  enough sample points to survive RDP simplification. */
export const SKETCH_ADDITION_RESAMPLE_SPACING_IN = 1;

/** RDP tolerance floor for additions; the full-room default of 3" collapses
 *  corners on alcove-length walls. */
export const SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN = 0.75;

/** Auto-close threshold for additions (inches). Smaller than the full-room
 *  default since alcove strokes are short and a 12" gap would over-aggressively
 *  close a partially-drawn outline. */
export const SKETCH_ADDITION_CLOSE_THRESHOLD_IN = 6;

/** Threshold to call the addition "touching" a wall (inches). */
export const SKETCH_TOUCH_THRESHOLD_IN = 12;

export interface ProcessStrokeOptions {
  /** Disable orthogonal snapping (Shift held). */
  freeAngles?: boolean;
  /** Snap grid in inches. Defaults to `SKETCH_SNAP_INCHES`. */
  snapInches?: number;
  /** Override the resample spacing (inches). */
  resampleSpacing?: number;
  /** Override the RDP tolerance (inches). The adaptive perimeter rule is
   *  bypassed entirely when this is set. */
  rdpTolerance?: number;
  /** Override the RDP tolerance floor (inches). The adaptive `perim / 200`
   *  rule still applies on top; this is just the lower bound. */
  minRdpToleranceIn?: number;
  /** Override the orthogonal snap window (degrees). */
  orthoWindowDeg?: number;
  /** Override the auto-close threshold (inches). */
  closeThresholdIn?: number;
  /** Override the minimum-area gate (sq inches). Defaults to
   *  `SKETCH_MIN_AREA_SQ_IN`; pass `SKETCH_ADDITION_MIN_AREA_SQ_IN` for
   *  alcove/closet sketches so a small closet isn't rejected as "too small". */
  minAreaSqIn?: number;
}

export type SketchFailReason =
  | "too-few-points"
  | "self-intersecting"
  | "too-small"
  | "degenerate";

export type SketchResult =
  | {
      ok: true;
      /** Cleaned polygon in inches. */
      points: Vec2[];
      /** 0..1 confidence score (how clean the snap was). */
      confidence: number;
      /** Human-readable notes for the UI. */
      warnings: string[];
    }
  | {
      ok: false;
      reason: SketchFailReason;
      message: string;
    };

export type UnionResult =
  | { ok: true; points: Vec2[]; snapped: boolean }
  | { ok: false; reason: "disjoint" | "self-intersecting" };

// ---------------------------------------------------------------------------
// Pipeline primitives
// ---------------------------------------------------------------------------

/** Resample a polyline to uniform arc-length spacing. */
export function resample(points: Vec2[], spacing: number): Vec2[] {
  if (points.length < 2 || spacing <= 0) return points.slice();
  const out: Vec2[] = [points[0]];
  let acc = 0;
  let prev = points[0];
  for (let i = 1; i < points.length; i++) {
    const cur = points[i];
    let segLen = distance(prev, cur);
    if (segLen === 0) continue;
    let from = prev;
    while (acc + segLen >= spacing) {
      const t = (spacing - acc) / segLen;
      const next: Vec2 = {
        x: from.x + (cur.x - from.x) * t,
        y: from.y + (cur.y - from.y) * t,
      };
      out.push(next);
      from = next;
      segLen = distance(from, cur);
      acc = 0;
    }
    acc += segLen;
    prev = cur;
  }
  const last = points[points.length - 1];
  const tail = out[out.length - 1];
  if (distance(tail, last) > spacing * 0.5) out.push(last);
  return out;
}

/** Ramer–Douglas–Peucker simplification. */
export function simplifyRDP(points: Vec2[], tolerance: number): Vec2[] {
  if (points.length < 3 || tolerance <= 0) return points.slice();
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [lo, hi] = stack.pop()!;
    if (hi - lo < 2) continue;
    const a = points[lo];
    const b = points[hi];
    let maxDist = 0;
    let idx = -1;
    for (let i = lo + 1; i < hi; i++) {
      const d = perpendicularDistance(points[i], a, b);
      if (d > maxDist) {
        maxDist = d;
        idx = i;
      }
    }
    if (idx >= 0 && maxDist > tolerance) {
      keep[idx] = true;
      stack.push([lo, idx]);
      stack.push([idx, hi]);
    }
  }
  const out: Vec2[] = [];
  for (let i = 0; i < points.length; i++) {
    if (keep[i]) out.push(points[i]);
  }
  return out;
}

function perpendicularDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return distance(p, a);
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  const tt = Math.max(0, Math.min(1, t));
  const proj: Vec2 = { x: a.x + tt * dx, y: a.y + tt * dy };
  return distance(p, proj);
}

/** Close the polyline if the last point is near the first. */
export function closeIfNear(
  points: Vec2[],
  thresholdIn: number,
  bboxFraction = SKETCH_CLOSE_BBOX_FRACTION
): Vec2[] {
  if (points.length < 3) return points.slice();
  const first = points[0];
  const last = points[points.length - 1];
  const d = distance(first, last);
  const bb = boundingBox(points);
  const diag = Math.hypot(bb.width, bb.height);
  const effective = Math.max(thresholdIn, diag * bboxFraction);
  if (d <= effective) {
    return points.slice(0, points.length - 1);
  }
  return points.slice();
}

interface Segment {
  a: Vec2;
  b: Vec2;
  angle: number;
}

function segmentsFromPolygon(points: Vec2[]): Segment[] {
  const segs: Segment[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    segs.push({ a, b, angle: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI });
  }
  return segs;
}

/** Normalize an angle to [-90, 90] so 180° ↔ 0° and -90° ↔ 90°. */
function normalizeAxisAngle(deg: number): number {
  let a = deg % 180;
  if (a > 90) a -= 180;
  if (a < -90) a += 180;
  return a;
}

/** Snap each segment to the nearest axis if within ±orthoWindowDeg. */
export function rectilinearize(
  points: Vec2[],
  opts: { freeAngles?: boolean; orthoWindowDeg?: number } = {}
): {
  points: Vec2[];
  snappedCount: number;
  customAngleCount: number;
} {
  const freeAngles = opts.freeAngles ?? false;
  const window = opts.orthoWindowDeg ?? SKETCH_ORTHO_SNAP_DEG;
  if (points.length < 3) {
    return { points: points.slice(), snappedCount: 0, customAngleCount: 0 };
  }
  if (freeAngles) {
    return {
      points: points.slice(),
      snappedCount: 0,
      customAngleCount: points.length,
    };
  }

  const segs = segmentsFromPolygon(points);
  const snapped = new Array<boolean>(segs.length).fill(false);
  // 0 = horizontal, 1 = vertical, -1 = free angle.
  const targetAxis = new Array<number>(segs.length).fill(-1);
  for (let i = 0; i < segs.length; i++) {
    const norm = normalizeAxisAngle(segs[i].angle);
    if (Math.abs(norm) <= window) {
      snapped[i] = true;
      targetAxis[i] = 0;
    } else if (Math.abs(Math.abs(norm) - 90) <= window) {
      snapped[i] = true;
      targetAxis[i] = 1;
    }
  }

  // Rebuild vertices so snapped segments are exactly axis-aligned. For each
  // vertex i (between seg (i-1) and seg i) we project one coordinate from the
  // previous vertex when the incoming segment is snapped. This yields a clean
  // L-shape from a slightly jagged scribble.
  const out: Vec2[] = points.map((p) => ({ ...p }));
  const n = out.length;
  // Iterate twice so a chain of snapped segments fully aligns.
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < n; i++) {
      const prevIdx = (i - 1 + n) % n;
      if (!snapped[prevIdx]) continue;
      const prevAxis = targetAxis[prevIdx];
      // Snapped incoming segment: vertex i shares one coord with vertex (i-1).
      if (prevAxis === 0) {
        out[i] = { x: out[i].x, y: out[prevIdx].y };
      } else if (prevAxis === 1) {
        out[i] = { x: out[prevIdx].x, y: out[i].y };
      }
    }
  }

  const snappedCount = snapped.filter(Boolean).length;
  const customAngleCount = snapped.length - snappedCount;
  return { points: out, snappedCount, customAngleCount };
}

/** Snap each vertex coordinate to the grid. */
export function snapPolygonToGrid(points: Vec2[], gridIn: number): Vec2[] {
  if (gridIn <= 0) return points.slice();
  return points.map((p) => ({ x: snap(p.x, gridIn), y: snap(p.y, gridIn) }));
}

/** Drop consecutive duplicate vertices and merge collinear runs. */
export function dedupeAndMergeCollinear(
  points: Vec2[],
  epsIn = 0.5
): Vec2[] {
  if (points.length < 3) return points.slice();
  // Drop near-duplicates (including the closing wraparound).
  const deduped: Vec2[] = [];
  for (const p of points) {
    const last = deduped[deduped.length - 1];
    if (!last || distance(last, p) > epsIn) deduped.push(p);
  }
  if (
    deduped.length >= 2 &&
    distance(deduped[0], deduped[deduped.length - 1]) <= epsIn
  ) {
    deduped.pop();
  }
  if (deduped.length < 3) return deduped;
  // Drop vertices that lie on the line between their neighbors.
  const keep = new Array<boolean>(deduped.length).fill(true);
  const n = deduped.length;
  for (let i = 0; i < n; i++) {
    const prev = deduped[(i - 1 + n) % n];
    const cur = deduped[i];
    const next = deduped[(i + 1) % n];
    const ax = cur.x - prev.x;
    const ay = cur.y - prev.y;
    const bx = next.x - cur.x;
    const by = next.y - cur.y;
    const cross = ax * by - ay * bx;
    const lenA = Math.hypot(ax, ay);
    const lenB = Math.hypot(bx, by);
    if (lenA === 0 || lenB === 0) {
      keep[i] = false;
      continue;
    }
    // |cross| / (lenA * lenB) ≈ sin(theta); near 0 means collinear.
    if (Math.abs(cross) / (lenA * lenB) < 1e-3) keep[i] = false;
  }
  const out = deduped.filter((_, i) => keep[i]);
  return out.length >= 3 ? out : deduped;
}

// ---------------------------------------------------------------------------
// Top-level pipeline
// ---------------------------------------------------------------------------

/** Run the full sketch pipeline on a raw stroke. */
export function processStroke(
  raw: Vec2[],
  opts: ProcessStrokeOptions = {}
): SketchResult {
  if (!raw || raw.length < 3) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — try again with a longer drag.",
    };
  }

  const snapInches = opts.snapInches ?? SKETCH_SNAP_INCHES;
  const spacing = opts.resampleSpacing ?? SKETCH_RESAMPLE_SPACING_IN;
  const closeThreshold = opts.closeThresholdIn ?? SKETCH_CLOSE_THRESHOLD_IN;
  const orthoWindow = opts.orthoWindowDeg ?? SKETCH_ORTHO_SNAP_DEG;
  const minRdpTol = opts.minRdpToleranceIn ?? SKETCH_MIN_RDP_TOLERANCE_IN;
  const minArea = opts.minAreaSqIn ?? SKETCH_MIN_AREA_SQ_IN;

  // 1) Resample at uniform spacing so RDP sees evenly distributed input.
  const resampled = resample(raw, spacing);

  // 2) RDP with adaptive tolerance.
  const bb = boundingBox(resampled);
  const perim = approximatePolylinePerimeter(resampled);
  const rdpTol =
    opts.rdpTolerance ??
    Math.max(minRdpTol, perim / 200);
  const simplified = simplifyRDP(resampled, rdpTol);

  if (simplified.length < 3) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — try again with a longer drag.",
    };
  }

  // 3) Auto-close if endpoint is near start.
  const closed = closeIfNear(simplified, closeThreshold);
  if (closed.length < 3) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — try again with a longer drag.",
    };
  }

  // 4) Drop colinear runs from the closed polygon (RDP can leave a few when
  //    the stroke had a near-straight kink).
  const trimmed = dedupeAndMergeCollinear(closed, Math.max(0.5, rdpTol / 4));
  if (trimmed.length < 3) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — try again with a longer drag.",
    };
  }

  // 5) Rectilinearize segments.
  const recti = rectilinearize(trimmed, {
    freeAngles: opts.freeAngles,
    orthoWindowDeg: orthoWindow,
  });

  // 6) Snap each vertex to the grid.
  const snapped = snapPolygonToGrid(recti.points, snapInches);

  // 7) Final dedupe + merge collinear so the grid snap can't introduce
  //    zero-length walls or stair-stepping.
  const cleaned = dedupeAndMergeCollinear(snapped, Math.max(0.5, snapInches / 2));

  if (cleaned.length < 3) {
    return {
      ok: false,
      reason: "degenerate",
      message: "Couldn't read that shape — try again.",
    };
  }

  // 8) Validate simple polygon.
  if (!isSimplePolygon(cleaned)) {
    return {
      ok: false,
      reason: "self-intersecting",
      message: "That shape crosses itself — try drawing without overlapping.",
    };
  }

  // 9) Minimum area gate.
  const area = polygonArea(cleaned);
  if (area < minArea) {
    return {
      ok: false,
      reason: "too-small",
      message: "Too small — try drawing a bit bigger.",
    };
  }

  // Confidence: fraction of orthogonal segments + room-size confidence.
  const total = recti.snappedCount + recti.customAngleCount;
  const orthoFrac = total > 0 ? recti.snappedCount / total : 1;
  const sizeFrac = Math.min(1, area / (50 * 144));
  const confidence = Math.max(0.1, 0.6 * orthoFrac + 0.4 * sizeFrac);

  const warnings: string[] = [];
  if (opts.freeAngles) {
    warnings.push("Kept your free angles");
  } else if (recti.snappedCount > 0 && recti.customAngleCount === 0) {
    warnings.push("Snapped to 90° corners");
  } else if (recti.snappedCount > 0) {
    warnings.push(
      `Snapped ${recti.snappedCount} wall${
        recti.snappedCount === 1 ? "" : "s"
      } to 90° — kept ${recti.customAngleCount} at the angle you drew`
    );
  }
  // Hint user that the stroke was auto-closed (no warning needed; closure is
  // expected). Reserved for future telemetry.
  void bb;

  return {
    ok: true,
    points: cleaned,
    confidence,
    warnings,
  };
}

/** Minimum length (inches) of an interior-wall stroke. */
export const SKETCH_WALL_MIN_LENGTH_IN = 12;

/** Snap an open polyline to the axes. Unlike {@link rectilinearize}, the
 *  path is not treated as a closed ring. */
export function rectilinearizeOpen(
  points: Vec2[],
  opts: { freeAngles?: boolean; orthoWindowDeg?: number } = {}
): {
  points: Vec2[];
  snappedCount: number;
  customAngleCount: number;
} {
  if (points.length < 2) {
    return { points: points.slice(), snappedCount: 0, customAngleCount: 0 };
  }
  if (opts.freeAngles) {
    return {
      points: points.slice(),
      snappedCount: 0,
      customAngleCount: Math.max(0, points.length - 1),
    };
  }
  const window = opts.orthoWindowDeg ?? SKETCH_ORTHO_SNAP_DEG;
  const nSeg = points.length - 1;
  const snapped = new Array<boolean>(nSeg).fill(false);
  const targetAxis = new Array<number>(nSeg).fill(-1);
  for (let i = 0; i < nSeg; i++) {
    const angle =
      (Math.atan2(points[i + 1].y - points[i].y, points[i + 1].x - points[i].x) *
        180) /
      Math.PI;
    const norm = normalizeAxisAngle(angle);
    if (Math.abs(norm) <= window) {
      snapped[i] = true;
      targetAxis[i] = 0;
    } else if (Math.abs(Math.abs(norm) - 90) <= window) {
      snapped[i] = true;
      targetAxis[i] = 1;
    }
  }
  const out: Vec2[] = points.map((p) => ({ ...p }));
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 1; i < out.length; i++) {
      if (!snapped[i - 1]) continue;
      if (targetAxis[i - 1] === 0) out[i] = { x: out[i].x, y: out[i - 1].y };
      else if (targetAxis[i - 1] === 1) out[i] = { x: out[i - 1].x, y: out[i].y };
    }
  }
  const snappedCount = snapped.filter(Boolean).length;
  return {
    points: out,
    snappedCount,
    customAngleCount: nSeg - snappedCount,
  };
}

/** Drop near-duplicate and collinear vertices on an open polyline. */
export function dedupeOpen(points: Vec2[], epsIn = 0.5): Vec2[] {
  if (points.length < 2) return points.slice();
  const deduped: Vec2[] = [];
  for (const p of points) {
    const last = deduped[deduped.length - 1];
    if (!last || distance(last, p) > epsIn) deduped.push({ ...p });
  }
  if (deduped.length < 3) return deduped;
  const keep = new Array<boolean>(deduped.length).fill(true);
  for (let i = 1; i < deduped.length - 1; i++) {
    const prev = deduped[i - 1];
    const cur = deduped[i];
    const next = deduped[i + 1];
    const ax = cur.x - prev.x;
    const ay = cur.y - prev.y;
    const bx = next.x - cur.x;
    const by = next.y - cur.y;
    const cross = ax * by - ay * bx;
    const lenA = Math.hypot(ax, ay);
    const lenB = Math.hypot(bx, by);
    if (lenA === 0 || lenB === 0 || Math.abs(cross) / (lenA * lenB) < 1e-3) {
      keep[i] = false;
    }
  }
  return deduped.filter((_, i) => keep[i]);
}

function openLength(pts: Vec2[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += distance(pts[i - 1], pts[i]);
  return total;
}

/** Clean a stroke into an open wall polyline. No auto-close and no area gate. */
export function processWallStroke(
  raw: Vec2[],
  opts: ProcessStrokeOptions = {}
): SketchResult {
  if (!raw || raw.length < 2) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — draw a longer wall.",
    };
  }
  const snapInches = opts.snapInches ?? SKETCH_SNAP_INCHES;
  const spacing = opts.resampleSpacing ?? SKETCH_ADDITION_RESAMPLE_SPACING_IN;
  const orthoWindow = opts.orthoWindowDeg ?? SKETCH_ORTHO_SNAP_DEG;
  const minRdpTol = opts.minRdpToleranceIn ?? SKETCH_ADDITION_MIN_RDP_TOLERANCE_IN;

  const resampled = resample(raw, spacing);
  const rdpTol = opts.rdpTolerance ?? Math.max(minRdpTol, openLength(resampled) / 200);
  const simplified = simplifyRDP(resampled, rdpTol);
  if (simplified.length < 2) {
    return {
      ok: false,
      reason: "too-few-points",
      message: "Stroke too short — draw a longer wall.",
    };
  }

  const recti = rectilinearizeOpen(simplified, {
    freeAngles: opts.freeAngles,
    orthoWindowDeg: orthoWindow,
  });
  const snapped = snapPolygonToGrid(recti.points, snapInches);
  const cleaned = dedupeOpen(snapped, Math.max(0.5, snapInches / 2));
  if (cleaned.length < 2) {
    return {
      ok: false,
      reason: "degenerate",
      message: "Couldn't read that wall — try again.",
    };
  }
  if (openLength(cleaned) < SKETCH_WALL_MIN_LENGTH_IN) {
    return {
      ok: false,
      reason: "too-small",
      message: "Too short — draw a longer wall.",
    };
  }

  const warnings: string[] = [];
  if (opts.freeAngles) warnings.push("Kept your free angles");
  else if (recti.snappedCount > 0 && recti.customAngleCount === 0) {
    warnings.push("Snapped to 90° corners");
  } else if (recti.snappedCount > 0) {
    warnings.push(
      `Snapped ${recti.snappedCount} wall${recti.snappedCount === 1 ? "" : "s"} to 90°`
    );
  }

  return {
    ok: true,
    points: cleaned,
    confidence: 0.8,
    warnings,
  };
}

function approximatePolylinePerimeter(pts: Vec2[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += distance(pts[i - 1], pts[i]);
  // Close back to start so adaptive RDP tolerance is similar for open and
  // closed strokes (a near-closed scribble is ~the same room).
  if (pts.length > 2) total += distance(pts[pts.length - 1], pts[0]);
  return total;
}

// ---------------------------------------------------------------------------
// Union helpers (used for the "addition" / closet flow)
// ---------------------------------------------------------------------------

/** Translate `addition` so its nearest edge to `room` touches `room`'s
 *  nearest wall. Used as a one-shot retry when the user drew the alcove just
 *  shy of the room outline. */
export function snapAdditionToRoomWall(
  room: Vec2[],
  addition: Vec2[]
): { points: Vec2[]; offset: Vec2; distance: number } {
  let best: {
    delta: Vec2;
    dist: number;
  } | null = null;
  for (const p of addition) {
    for (let i = 0; i < room.length; i++) {
      const a = room[i];
      const b = room[(i + 1) % room.length];
      const candidate = nearestPointOnSegment(p, a, b);
      const dx = candidate.x - p.x;
      const dy = candidate.y - p.y;
      const d = Math.hypot(dx, dy);
      if (!best || d < best.dist) {
        best = { delta: { x: dx, y: dy }, dist: d };
      }
    }
  }
  if (!best) {
    return {
      points: addition.slice(),
      offset: { x: 0, y: 0 },
      distance: Infinity,
    };
  }
  return {
    points: addition.map((p) => ({
      x: p.x + best!.delta.x,
      y: p.y + best!.delta.y,
    })),
    offset: best.delta,
    distance: best.dist,
  };
}

/** Union a sketch with the existing room polygon.
 *
 *  Returns `{ ok: true }` for a single contiguous polygon, or `{ ok: false }`
 *  if the addition is disjoint or produces a self-intersecting result.
 *
 *  If the initial union is disjoint, the caller can retry once after calling
 *  {@link snapAdditionToRoomWall} — handled here automatically when
 *  `touchThresholdIn` is provided. */
export function unionWithRoom(
  roomPoints: Vec2[],
  sketchPoints: Vec2[],
  opts: { touchThresholdIn?: number } = {}
): UnionResult {
  const direct = unionPolygons(roomPoints, sketchPoints);
  if (direct && isSimplePolygon(direct)) {
    return { ok: true, points: direct, snapped: false };
  }

  // Snap-to-nearest-wall retry.
  const threshold = opts.touchThresholdIn ?? SKETCH_TOUCH_THRESHOLD_IN;
  const snapped = snapAdditionToRoomWall(roomPoints, sketchPoints);
  if (snapped.distance <= threshold) {
    const retry = unionPolygons(roomPoints, snapped.points);
    if (retry && isSimplePolygon(retry)) {
      return { ok: true, points: retry, snapped: true };
    }
  }

  if (!direct) return { ok: false, reason: "disjoint" };
  return { ok: false, reason: "self-intersecting" };
}
